import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from '../environment';
import { RootState } from '../store';
import { Property, QueryParams } from '../interfaces';
import { objectToString } from '../helpers';

export const PropertyAPI = createApi({
  reducerPath: 'propertyAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: env.apiUrl + '/property',
    prepareHeaders: (headers, { getState }) => {
      const { accessToken } = (getState() as RootState).auth;
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return headers;
    }
  }),
  tagTypes: ['Property'],
  endpoints: (build) => ({
    fetchProperties: build.query<Property[], QueryParams>({
      query: (params) => '/?' + objectToString(params),
      providesTags: result => result
        ? [...result.map(({ id }) => ({ type: 'Property' as const, id }))]
        : ['Property'],
    }),
    fetchProperty: build.query<Property, string>({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) =>
        result ? [{ type: 'Property' as const, id }] : ['Property'],
    }),
    createProperty: build.mutation<Property, Partial<Property>>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Property'],
    }),
    addImage: build.mutation<Property, { id: string; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/image/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        result ? [{ type: 'Property' as const, id }] : ['Property'],
    }),
    addVideo: build.mutation<Property, { id: string; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/video/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        result ? [{ type: 'Property' as const, id }] : ['Property'],
    }),
    addGallery: build.mutation<Property, { id: string; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/gallery/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        result ? [{ type: 'Property' as const, id }] : ['Property'],
    }),
    deleteImage: build.mutation<Property, string>({
      query: (id) => ({
        url: `/gallery/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Property'],
    }),
    updateProperty: build
      .mutation<Property, { id: string; body: Partial<Property> }>({
        query: ({ id, body }) => ({
          url: `/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (result, error, { id }) =>
          result ? [{ type: 'Property' as const, id }] : ['Property'],
      }),
    deleteProperty: build.mutation<Property, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) =>
        result ? [{ type: 'Property' as const, id }] : ['Property'],
    }),
  }),
});

export const {
  useFetchPropertiesQuery,
  useFetchPropertyQuery,
  useCreatePropertyMutation,
  useAddImageMutation,
  useAddVideoMutation,
  useAddGalleryMutation,
  useDeleteImageMutation,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,
} = PropertyAPI;